import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Home, Maximize, BedDouble, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProjectCarousel from '@/components/ProjectCarousel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}`);
      setProject(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-stone-500">Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 pt-24">
        <p className="text-stone-500">Project not found</p>
        <Button asChild variant="outline">
          <Link to="/projects">Back to Projects</Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="project-detail-page" className="pt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <Button
          asChild
          variant="ghost"
          data-testid="back-to-projects-button"
          className="mb-8 text-stone-600 hover:text-emerald-900"
        >
          <Link to="/projects">
            <ArrowLeft className="w-4 h-4 mr-2" />
            BACK TO PROJECTS
          </Link>
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          <div>
            <ProjectCarousel 
              images={project.gallery_images && project.gallery_images.length > 0 
                ? project.gallery_images 
                : [project.image_url]
              }
              projectTitle={project.title}
            />
          </div>

          <div>
            <div className="inline-block bg-emerald-900 text-white px-4 py-2 text-xs uppercase tracking-widest mb-4">
              {project.status}
            </div>
            <h1 className="text-4xl sm:text-5xl font-playfair font-bold text-stone-900 mb-4 tracking-tight">
              {project.title}
            </h1>
            <div className="flex items-center gap-2 text-stone-600 mb-6">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">{project.location}</span>
            </div>

            {project.price_range && (
              <div className="mb-6">
                <p className="text-3xl font-playfair font-bold text-amber-600">{project.price_range}</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 mb-8">
              {project.type && (
                <div className="p-4 bg-stone-50 border border-stone-200">
                  <Home className="w-6 h-6 text-emerald-900 mb-2" />
                  <p className="text-xs text-stone-500 uppercase tracking-widest">Type</p>
                  <p className="text-sm font-bold text-stone-900 mt-1">{project.type}</p>
                </div>
              )}
              {project.area && (
                <div className="p-4 bg-stone-50 border border-stone-200">
                  <Maximize className="w-6 h-6 text-emerald-900 mb-2" />
                  <p className="text-xs text-stone-500 uppercase tracking-widest">Area</p>
                  <p className="text-sm font-bold text-stone-900 mt-1">{project.area}</p>
                </div>
              )}
              {project.bedrooms && (
                <div className="p-4 bg-stone-50 border border-stone-200">
                  <BedDouble className="w-6 h-6 text-emerald-900 mb-2" />
                  <p className="text-xs text-stone-500 uppercase tracking-widest">Bedrooms</p>
                  <p className="text-sm font-bold text-stone-900 mt-1">{project.bedrooms}</p>
                </div>
              )}
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-playfair font-bold text-stone-900 mb-4">Description</h3>
              <p className="text-stone-700 leading-relaxed">{project.description}</p>
            </div>

            {project.amenities && project.amenities.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-playfair font-bold text-stone-900 mb-4">Amenities</h3>
                <div className="grid grid-cols-2 gap-3">
                  {project.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-900" />
                      <span className="text-stone-700">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              asChild
              data-testid="inquire-button"
              className="w-full bg-emerald-900 hover:bg-emerald-800 text-white px-8 py-6 uppercase tracking-widest text-xs font-bold transition-all duration-300"
            >
              <Link to="/contact">INQUIRE ABOUT THIS PROPERTY</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;